import { useQuery } from '@tanstack/react-query'
import { useSchool } from '@/components/context/school-context'
import React from 'react'
import { useAuth, useUser } from '@clerk/nextjs'
import { getSchool, listSchools, SchoolResponseSchema, type School } from '@/lib/api/schools'
import { isApiError, useBackendClient } from '@/lib/backend-client'
import { isSuperAdminFromClient } from '@/lib/utils/roles'
import { schoolDetailKeys } from '@/lib/query/keys/school-detail'
import { STALE_TIME } from '@/lib/query/stale-times'

export type { School } from '@/lib/api/schools'

export const SCHOOLS_QUERY_KEY = ['schools'] as const

// Custom hook to fetch schools list only
export function useSchools() {
  // Always call hooks at the top level
  const authResult = useAuth()
  const { userId } = authResult
  const client = useBackendClient()
  const { currentSchool: contextSchool, changeSchool } = useSchool()

  // `GET /v1/schools` is super-admin only (backend `requireSuperAdmin`), so this
  // read is gated on the same claim. Previously every school admin fired it and
  // took a 403 on each page — see the `enabled` comment below for why that 403
  // was not merely noise.
  const isSuperAdmin = isSuperAdminFromClient(authResult)

  // Define async functions outside of React Query to avoid hook dependency issues
  const fetchSchoolList = React.useCallback(async (): Promise<School[]> => {
    // A refused read settles on an empty list rather than an error: an error
    // is retried, and `isLoading` (`isPending && isFetching`) flips true on
    // every retry. Consumers that gate render on it then unmount and lose
    // their local state.
    try {
      return await listSchools(client)
    } catch (error) {
      if (isApiError(error)) return []
      throw error
    }
  }, [client])

  const fetchSchoolDetail = React.useCallback(
    async (schoolId: string) => {
      if (!schoolId) return null
      const result = await getSchool(client, schoolId, SchoolResponseSchema)
      return result.data
    },
    [client]
  )

  // Always call useQuery hooks unconditionally
  const {
    data: schools,
    isLoading,
    isError
  } = useQuery({
    queryKey: SCHOOLS_QUERY_KEY,
    queryFn: fetchSchoolList,
    // School admins are not permitted to list every school and never were: the
    // 403 left `schools` undefined for them regardless, so skipping the call is
    // behavior-preserving. It also stops the failed query from parking in
    // `isPending`, whose `isLoading` re-flip unmounted the six school surfaces
    // that gate render on it — closing the App Users detail dialog the moment
    // opening it mounted another `useSchools()` observer.
    enabled: !!userId && isSuperAdmin,
    staleTime: 5 * 60 * 1000 // Consider data fresh for 5 minutes
  })

  // Always call this hook - use enabled option for conditional fetching
  const { data: detailedSchoolResponse, isSuccess: detailedDataSuccess } = useQuery({
    queryKey: schoolDetailKeys.detail(contextSchool?.id ?? null),
    queryFn: () => fetchSchoolDetail(contextSchool?.id || ''),
    enabled: !!userId && !!contextSchool?.id,
    staleTime: STALE_TIME.long
  })

  // Get the current school from the fetched schools data
  // Use detailed school data if available, otherwise use from schools list
  let currentSchool = schools?.find((school: School) => school.id === contextSchool?.id) || contextSchool

  // If we have detailed data, merge it with the basic school data to ensure we have all properties
  if (detailedDataSuccess && detailedSchoolResponse) {
    currentSchool = {
      ...currentSchool,
      ...detailedSchoolResponse
    }
    // console.log('Final merged currentSchool with systemGroups:', currentSchool)
  }

  return {
    schools,
    isLoading,
    isError,
    currentSchool,
    changeSchool
  }
}
